import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { chatShellActions, useChatShellStore } from './chat-shell.store'

const SCROLL_BOTTOM_THRESHOLD_PX = 32

function isNearBottom(element: HTMLDivElement): boolean {
  return (
    element.scrollHeight - element.clientHeight - element.scrollTop <=
    SCROLL_BOTTOM_THRESHOLD_PX
  )
}

interface UseChatMessagesScrollParams {
  scrollContextId: string
  isMessagesLoading: boolean
  isCurrentChatGenerating: boolean
  isAssistantTyping: boolean
  pendingMessage: string | null
  streamingAssistantMessage: string | null
  messagesLength: number
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => Promise<unknown>
}

export function useChatMessagesScroll({
  scrollContextId,
  isMessagesLoading,
  isCurrentChatGenerating,
  isAssistantTyping,
  pendingMessage,
  streamingAssistantMessage,
  messagesLength,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: UseChatMessagesScrollParams) {
  const store = useChatShellStore()
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const previousScrollContextIdRef = useRef<string | null>(null)
  const shouldRestoreScrollRef = useRef(true)
  const shouldStickToBottomRef = useRef(true)
  // Distance from bottom captured before loading older messages, so the
  // viewport stays anchored on the same message after the prepend.
  const prependAnchorRef = useRef<number | null>(null)

  // Must be a layout effect (not useEffect) and declared BEFORE the restore
  // layout effect below: it has to set the flag in the same commit, before the
  // restore runs. Otherwise, for cached chats (no isMessagesLoading transition
  // to re-trigger it) the restore never fires and the saved position is lost.
  useLayoutEffect(() => {
    if (previousScrollContextIdRef.current === scrollContextId) {
      return
    }

    previousScrollContextIdRef.current = scrollContextId
    shouldRestoreScrollRef.current = true
  }, [scrollContextId])

  useLayoutEffect(() => {
    if (!shouldRestoreScrollRef.current) {
      return
    }

    const container = messagesContainerRef.current
    if (!container || isMessagesLoading) {
      return
    }

    if (pendingMessage !== null || isCurrentChatGenerating) {
      shouldStickToBottomRef.current = false
      setShowScrollToBottom(false)
      shouldRestoreScrollRef.current = false
      return
    }

    const savedScrollTop = store.scrollTopByChatContextId[scrollContextId]
    container.scrollTop =
      savedScrollTop ??
      Math.max(container.scrollHeight - container.clientHeight, 0)
    shouldStickToBottomRef.current = isNearBottom(container)
    setShowScrollToBottom(!shouldStickToBottomRef.current)
    shouldRestoreScrollRef.current = false
  }, [
    isCurrentChatGenerating,
    isMessagesLoading,
    pendingMessage,
    scrollContextId,
    store.scrollTopByChatContextId,
  ])

  useEffect(() => {
    if (shouldRestoreScrollRef.current || !shouldStickToBottomRef.current) {
      return
    }

    const container = messagesContainerRef.current
    if (!container) {
      return
    }

    container.scrollTop = container.scrollHeight
    chatShellActions.setScrollTop(scrollContextId, container.scrollTop)
    setShowScrollToBottom(false)
  }, [
    isAssistantTyping,
    messagesLength,
    pendingMessage,
    scrollContextId,
    streamingAssistantMessage,
  ])

  useLayoutEffect(() => {
    const container = messagesContainerRef.current
    if (!container || prependAnchorRef.current === null) {
      return
    }

    // Older messages were prepended: restore the previous distance-from-bottom
    // so the message the user was looking at stays in place.
    container.scrollTop = container.scrollHeight - prependAnchorRef.current
    prependAnchorRef.current = null
  }, [messagesLength])

  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current
    if (!container) {
      return
    }

    if (container.scrollTop <= 80 && hasNextPage && !isFetchingNextPage) {
      // Anchor on distance-from-bottom; restored after older messages prepend.
      prependAnchorRef.current = container.scrollHeight - container.scrollTop
      void fetchNextPage()
    }

    shouldStickToBottomRef.current = isNearBottom(container)
    chatShellActions.setScrollTop(scrollContextId, container.scrollTop)
    setShowScrollToBottom(!shouldStickToBottomRef.current)
  }

  const handleScrollToBottom = () => {
    const container = messagesContainerRef.current
    if (!container) {
      return
    }

    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    })
    shouldStickToBottomRef.current = false
    setShowScrollToBottom(false)
    chatShellActions.setScrollTop(scrollContextId, container.scrollHeight)
  }

  const setShouldStickToBottom = (stick: boolean) => {
    shouldStickToBottomRef.current = stick
  }

  return {
    messagesContainerRef,
    showScrollToBottom,
    handleMessagesScroll,
    handleScrollToBottom,
    setShouldStickToBottom,
  }
}
